import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { USER_SESSION_QUERY_KEY, useUserSession } from "@/hooks/use-user-session";

interface UserProfile {
  full_name: string | null;
  email: string;
  user_type: string | null;
  avatar_url?: string | null;
}

interface RoleContextType {
  userRole: string | null;
  activeRole: string | null;
  allRoles: string[];
  userProfile: UserProfile | null;
  isLoading: boolean;
  /**
   * Explicit tri-state role resolution result:
   *  - 'loading'  : get_my_context has not resolved yet
   *  - 'resolved' : resolution complete and the user has at least one role
   *  - 'no_role'  : resolution complete and the user has NO role in user_roles
   *
   * profiles.user_type is intentionally NOT consulted. user_roles (surfaced
   * via get_my_context) is the single source of truth for role resolution.
   */
  roleResolution: 'loading' | 'resolved' | 'no_role';
  userId: string | null;
  refetchProfile: () => void;
  setActiveRole: (role: string) => void;
  needsRoleSelection: boolean;
  clearRoleSelection: () => void;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

const ACTIVE_ROLE_KEY = "lurneax_active_role";

export function RoleProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data: session, isLoading } = useUserSession();

  // Role source of truth = user_roles (via get_my_context).
  // profiles.user_type is deprecated and MUST NOT be used as a fallback —
  // it contradicts user_roles on several accounts and masks missing role rows.
  const allRoles = useMemo(() => session?.roles ?? [], [session?.roles]);

  const userProfile = useMemo<UserProfile | null>(() => {
    if (!session?.userId) return null;
    return {
      full_name: session.fullName,
      email: session.email ?? "",
      user_type: session.userType,
      avatar_url: session.avatarUrl,
    };
  }, [session?.userId, session?.fullName, session?.email, session?.userType, session?.avatarUrl]);

  const [activeRole, setActiveRoleState] = useState<string | null>(null);
  const [needsRoleSelection, setNeedsRoleSelection] = useState(false);

  // Sync active-role state with the current roles list
  useEffect(() => {
    if (isLoading) return;

    if (!session?.userId) {
      setActiveRoleState(null);
      setNeedsRoleSelection(false);
      return;
    }

    const storedRole = localStorage.getItem(ACTIVE_ROLE_KEY);
    if (storedRole && !allRoles.includes(storedRole)) {
      localStorage.removeItem(ACTIVE_ROLE_KEY);
    }
    if (storedRole && allRoles.includes(storedRole)) {
      setActiveRoleState(storedRole);
      setNeedsRoleSelection(false);
    } else if (allRoles.length === 1) {
      setActiveRoleState(allRoles[0]);
      localStorage.setItem(ACTIVE_ROLE_KEY, allRoles[0]);
      setNeedsRoleSelection(false);
    } else if (allRoles.length > 1) {
      setActiveRoleState(null);
      setNeedsRoleSelection(true);
    }
  }, [isLoading, session?.userId, allRoles]);

  const setActiveRole = useCallback((role: string) => {
    setActiveRoleState(role);
    localStorage.setItem(ACTIVE_ROLE_KEY, role);
    setNeedsRoleSelection(false);
  }, []);

  const clearRoleSelection = useCallback(() => {
    localStorage.removeItem(ACTIVE_ROLE_KEY);
    setActiveRoleState(null);
    setNeedsRoleSelection(false);
  }, []);

  // Single authoritative auth-state listener — invalidates the shared
  // user-session query so every consumer refreshes from one fetch.
  useEffect(() => {
    let initialLoad = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (initialLoad) {
        initialLoad = false;
        return;
      }

      if (event === "SIGNED_OUT") {
        localStorage.removeItem(ACTIVE_ROLE_KEY);
        setActiveRoleState(null);
        setNeedsRoleSelection(false);
      }

      // SIGNED_IN is deliberately NOT invalidated here. LandingPage owns
      // the sign-in critical path and primes the ["user-session"] cache
      // via setQueryData immediately after get_my_context resolves.
      // Invalidating from this listener races LandingPage's prime and
      // forces a redundant second RPC on the destination route
      // (invalidateQueries sets isInvalidated=true, which setQueryData
      // does NOT clear). Skip TOKEN_REFRESHED / INITIAL_SESSION for the
      // same reason a token refresh must not cascade-refetch.
      if (event === "SIGNED_OUT" || event === "USER_UPDATED") {
        // Defer to avoid running inside the auth callback
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: USER_SESSION_QUERY_KEY });
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const refetchProfile = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: USER_SESSION_QUERY_KEY });
  }, [queryClient]);

  // userRole is the activeRole (for backward compatibility with all existing code)
  const userRole = activeRole || allRoles[0] || null;

  // Tri-state role resolution. Do NOT read profiles.user_type here.
  const roleResolution: 'loading' | 'resolved' | 'no_role' = isLoading
    ? 'loading'
    : !session?.userId
      ? 'loading'
      : allRoles.length === 0
        ? 'no_role'
        : 'resolved';

  return (
    <RoleContext.Provider value={{
      userRole,
      activeRole,
      allRoles,
      userProfile,
      isLoading,
      roleResolution,
      userId: session?.userId ?? null,
      refetchProfile,
      setActiveRole,
      needsRoleSelection,
      clearRoleSelection,
    }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}
